/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DATE_FORMAT } from '@/constants';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit (même piège que `validationInformationsNaissance`/`formatters`).
dayjs.extend(customParseFormat);

/**
 * Valide les sections « INFORMATIONS SUR LE MARIAGE » et « CONTRAT DE MARIAGE ».
 *
 * Obligatoires : date de la célébration et commune où elle a eu lieu. La date
 * doit être valide, non future, et antérieure ou égale à celle de l'acte — un
 * acte ne peut pas être dressé avant la célébration qu'il constate.
 *
 * ⚠️ L'officier d'état civil n'est PAS obligatoire : une partie du fonds repris
 * n'en porte pas (1156 actes sur 2809 ont un nom de signataire, 371 une
 * qualité). L'exiger empêcherait de numériser les autres.
 *
 * ⚠️ La date du contrat n'est contrôlée que si la case est cochée : décochée,
 * l'API purge la date, il n'y a donc rien à exiger.
 *
 * Les clés correspondent aux `name` des champs des deux blocs.
 */
const validationInformationsEvenementMariage = (data: Record<string, any>) => {
  const errors = {
    dateMariage: '',
    heureMariage: '',
    minuteMariage: '',
    adresseMariage: '',
    dateContratMariage: '',
  };

  const dateMariage = dayjs(data?.dateMariage, DATE_FORMAT, true);

  if (_.isEmpty(data?.dateMariage)) {
    errors.dateMariage = 'La date du mariage est obligatoire';
  } else if (!dateMariage.isValid()) {
    errors.dateMariage = 'La date du mariage est invalide';
  } else if (dateMariage.isAfter(dayjs(), 'day')) {
    errors.dateMariage =
      "La date du mariage ne peut pas être postérieure à aujourd'hui";
  } else if (!_.isEmpty(data?.dateActe)) {
    const dateActe = dayjs(data.dateActe, DATE_FORMAT, true);
    if (dateActe.isValid() && dateMariage.isAfter(dateActe, 'day')) {
      errors.dateMariage =
        "La date du mariage doit être antérieure ou égale à la date de l'acte";
    }
  }

  if (!_.isNil(data?.heureMariage) && _.isNil(data?.minuteMariage)) {
    errors.minuteMariage = 'Les minutes sont obligatoires';
  }

  if (!_.isNil(data?.minuteMariage) && _.isNil(data?.heureMariage)) {
    errors.heureMariage = "L'heure est obligatoire";
  }

  if (_.isEmpty(data?.adresseMariage?.libelleCommune)) {
    errors.adresseMariage = 'Le lieu du mariage est obligatoire';
  }

  if (data?.contratMariage && !_.isEmpty(data?.dateContratMariage)) {
    const dateContrat = dayjs(data.dateContratMariage, DATE_FORMAT, true);

    if (!dateContrat.isValid()) {
      errors.dateContratMariage = 'La date du contrat est invalide';
    } else if (
      dateMariage.isValid() &&
      dateContrat.isAfter(dateMariage, 'day')
    ) {
      errors.dateContratMariage =
        'Le contrat doit être reçu avant ou le jour du mariage';
    }
  }

  return _.pickBy(errors, (value) => value !== '');
};

export default validationInformationsEvenementMariage;
